import { useEffect, useMemo, useState } from "react";
import { DAY_MS, getDateFromLocalKey, getWeekDays, getWeekStart, toLocalDateKey } from "../utils/datetime";
import { ScheduledMatch } from "../pages/common/calendar/matchLayout";

// Gestisce lo stato "settimana visualizzata" + "giorno selezionato" del
// calendario e li riconcilia quando cambiano i match programmati (es. dopo un
// filtro o un cambio stagione). Gli effetti di riconciliazione sono mantenuti
// identici a prima: vivevano nel componente pagina.
export const useSelectedWeek = (scheduledMatches: ScheduledMatch[]) => {
  const matchesByDay = useMemo(() => {
    const map = new Map<string, ScheduledMatch[]>();
    scheduledMatches.forEach((item) => {
      map.set(item.dayKey, [...(map.get(item.dayKey) ?? []), item]);
    });
    return map;
  }, [scheduledMatches]);

  // Giorno da aprire di default: oggi se ha match, altrimenti il primo giorno
  // futuro con match, altrimenti (tutti i match passati) il primo in assoluto.
  // I dayKey "YYYY-MM-DD" si ordinano cronologicamente come stringhe.
  const defaultDayKey = useMemo(() => {
    if (!scheduledMatches.length) return "";
    const todayKey = toLocalDateKey(new Date());
    if (matchesByDay.has(todayKey)) return todayKey;
    const upcoming = scheduledMatches.find((item) => item.dayKey >= todayKey);
    return upcoming ? upcoming.dayKey : scheduledMatches[0].dayKey;
  }, [scheduledMatches, matchesByDay]);

  const defaultDate = useMemo(
    () => (defaultDayKey ? getDateFromLocalKey(defaultDayKey) : new Date()),
    [defaultDayKey]
  );

  const [weekStart, setWeekStart] = useState(() => getWeekStart(defaultDate));
  const [selectedDayKey, setSelectedDayKey] = useState(defaultDayKey);

  useEffect(() => {
    if (!scheduledMatches.length) {
      setSelectedDayKey("");
      setWeekStart(getWeekStart(new Date()));
      return;
    }
    if (!matchesByDay.has(selectedDayKey)) {
      setSelectedDayKey(defaultDayKey);
      setWeekStart(getWeekStart(defaultDate));
    }
  }, [defaultDate, defaultDayKey, matchesByDay, scheduledMatches.length, selectedDayKey]);

  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);
  const weekDayKeys = useMemo(() => weekDays.map(toLocalDateKey), [weekDays]);

  useEffect(() => {
    // Quando selectedDayKey è vuoto è il caricamento iniziale: lo gestisce
    // l'effetto sopra (giorno di default). Qui ci occupiamo solo della
    // navigazione tra settimane, scegliendo un giorno con match nella settimana
    // visibile se quello selezionato non c'è più / non ne ha.
    if (!selectedDayKey) return;
    const selectableDayKey = weekDayKeys.find((dayKey) => matchesByDay.has(dayKey));
    if (selectableDayKey && !weekDayKeys.includes(selectedDayKey)) {
      setSelectedDayKey(selectableDayKey);
      return;
    }
    if (selectableDayKey && !matchesByDay.has(selectedDayKey)) {
      setSelectedDayKey(selectableDayKey);
    }
  }, [matchesByDay, selectedDayKey, weekDayKeys]);

  const selectedDayMatches = useMemo(
    () => (weekDayKeys.includes(selectedDayKey) ? matchesByDay.get(selectedDayKey) ?? [] : []),
    [matchesByDay, selectedDayKey, weekDayKeys]
  );

  const selectedDate =
    selectedDayKey && weekDayKeys.includes(selectedDayKey) ? getDateFromLocalKey(selectedDayKey) : null;

  const weekRangeLabel = `${weekDays[0].toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
  })} - ${weekDays[6].toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
  })}`;

  const goPrevWeek = () => setWeekStart((current) => new Date(current.getTime() - 7 * DAY_MS));
  const goNextWeek = () => setWeekStart((current) => new Date(current.getTime() + 7 * DAY_MS));

  return {
    matchesByDay,
    weekDays,
    weekDayKeys,
    selectedDayKey,
    setSelectedDayKey,
    selectedDayMatches,
    selectedDate,
    weekRangeLabel,
    goPrevWeek,
    goNextWeek,
  };
};
